import { useCallback, useEffect, useRef, useState } from "react";

interface UsePharmacySelectionProps {
	confirmedValue?: string;
	dismissValue?: string;
	onConfirm: (vendorId: string) => void;
	isOpen: boolean;
}

interface UsePharmacySelectionReturn {
	previewId: number | null;
	confirmedId: number | null;
	announcementMessage: string;
	onPreviewSelect: (vendorId?: string) => void;
	onConfirmSelection: () => void;
	onDismiss: () => void;
	/** Deselects the previewed pharmacy card, e.g. when a group row takes over. */
	onClearPreview: () => void;
}

export function usePharmacySelection({
	confirmedValue,
	dismissValue,
	onConfirm,
	isOpen,
}: UsePharmacySelectionProps): UsePharmacySelectionReturn {
	const confirmedId = confirmedValue
		? Number.isFinite(Number(confirmedValue))
			? Number(confirmedValue)
			: null
		: null;

	const dismissId = dismissValue
		? Number.isFinite(Number(dismissValue))
			? Number(dismissValue)
			: null
		: null;

	const [previewId, setPreviewId] = useState<number | null>(confirmedId);
	const [announcementMessage, setAnnouncementMessage] = useState("");
	const prevIsOpen = useRef(isOpen);

	useEffect(() => {
		if (prevIsOpen.current && !isOpen) {
			setPreviewId(dismissId);
			setAnnouncementMessage("");
		}
		if (!prevIsOpen.current && isOpen) {
			setPreviewId(confirmedId);
		}
		prevIsOpen.current = isOpen;
	}, [isOpen, dismissId, confirmedId]);

	useEffect(() => {
		setPreviewId(confirmedId);
	}, [confirmedId]);

	const onPreviewSelect = useCallback((vendorId?: string) => {
		if (!vendorId) return;
		const id = Number(vendorId);
		if (!Number.isFinite(id)) return;
		setPreviewId(id);
		setAnnouncementMessage(`Pharmacy ${id} selected. Click confirm to apply.`);
	}, []);

	const onConfirmSelection = useCallback(() => {
		if (previewId === null) return;
		onConfirm(String(previewId));
		setAnnouncementMessage(`Pharmacy ${previewId} confirmed.`);
	}, [previewId, onConfirm]);

	const onDismiss = useCallback(() => {
		setPreviewId(confirmedId);
		setAnnouncementMessage("Selection cancelled.");
	}, [confirmedId]);

	const onClearPreview = useCallback(() => {
		setPreviewId(null);
	}, []);

	return {
		previewId,
		confirmedId,
		announcementMessage,
		onPreviewSelect,
		onConfirmSelection,
		onDismiss,
		onClearPreview,
	};
}
